import { Box } from 'grommet'
import { GetServerSideProps } from 'next'

import { useAuth } from 'hooks/useAuth'
import { fetchEmotions } from 'util/emotions'
import Layout from 'components/layout/Layout'
import { supabaseClient } from 'supabaseClient'
import { isGroupRestricted } from 'util/scopes'
import MoodPlotProvider from 'hooks/useMoodPlot'
import PlotInGroup from 'components/group/PlotInGroup'
import { CommonPageProps, createPage } from 'util/ssp'
import { groupSelect, isFutureGroup } from 'util/group'
import { BasicEmotion, ExistingGroupWithTags } from 'db-tables'
import FutureGroupLayout from 'components/layout/FutureGroupLayout'
import PrivateGroupLayout from 'components/layout/PrivateGroupLayout'
import GroupDescriptionInfo from 'components/group/GroupDescriptionInfo'
import ExistingMoodsFetcher from 'components/group/ExistingMoodsFetcher'

type Query = {
  shortCode: string
}
type PageProps = CommonPageProps & {
  group: ExistingGroupWithTags
  emotions: BasicEmotion[]
}

export const getServerSideProps: GetServerSideProps<Partial<PageProps>, Query> = async function (
  ctx,
) {
  return await createPage<PageProps>(ctx, {
    loadData: async (props) => {
      const { data: group, error } = await supabaseClient
        .from<ExistingGroupWithTags>('groups')
        .select(groupSelect)
        .eq('short_code', `${ctx.query.shortCode}`)
        .single()

      if (error) {
        console.error(error)
        throw error
      }

      const emotions = await fetchEmotions()
      const response = isGroupRestricted(props, group)
      if (response) return response
      return { props: { ...props, group, emotions } }
    },
  })
}

export const GroupPage = ({ group, emotions }: PageProps): JSX.Element => {
  const { user } = useAuth()
  if (!group) return <PrivateGroupLayout />
  if (isFutureGroup(group)) return <FutureGroupLayout group={group} />
  return (
    <Layout title={group.name} mainProps={{ align: 'start', justify: 'start' }}>
      <MoodPlotProvider group={group} initialAnon={!user}>
        <Box fill="horizontal" align="center" gap="small" margin={{ bottom: 'large' }}>
          <GroupDescriptionInfo group={group} linkResults />
          <PlotInGroup showResults redirectToResults group={group} emotions={emotions} />
        </Box>
        <ExistingMoodsFetcher groupId={group.id} />
      </MoodPlotProvider>
    </Layout>
  )
}

export default GroupPage
